/**
 * Command Palette — Brand Book §2.15
 * Cmd+K / Ctrl+K trigger, 480px card, grouped results, keyboard navigation
 * Feature component — depends on uiStore, themeStore, react-query, react-router
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Search, Grid, Briefcase, Upload, FileText, Settings,
  Shield, Plus, Moon, Sun, Zap,
} from 'react-feather';
import { useUIStore } from '../../stores/uiStore';
import { useThemeStore } from '../../stores/themeStore';
import { useCheckoutStore } from '../../stores/checkoutStore';
import { useQueryClient } from '@tanstack/react-query';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  category: 'page' | 'deal' | 'action';
  onExecute: () => void;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { commandPaletteOpen, closeCommandPalette, openModal } = useUIStore();
  const { theme, toggleTheme } = useThemeStore();
  const openCheckout = useCheckoutStore((s) => s.openCheckout);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Static page items
  const pageItems: CommandItem[] = useMemo(() => [
    { id: 'p-dashboard', label: 'Dashboard', icon: <Grid size={16} />, category: 'page', onExecute: () => navigate('/dashboard') },
    { id: 'p-deals', label: 'My deals', icon: <Briefcase size={16} />, category: 'page', onExecute: () => navigate('/deals') },
    { id: 'p-upload', label: 'Upload', icon: <Upload size={16} />, category: 'page', onExecute: () => navigate('/upload') },
    { id: 'p-documents', label: 'Documents', icon: <FileText size={16} />, category: 'page', onExecute: () => navigate('/documents') },
    { id: 'p-settings', label: 'Settings', icon: <Settings size={16} />, category: 'page', onExecute: () => navigate('/settings') },

    { id: 'p-admin', label: 'Admin', icon: <Shield size={16} />, category: 'page', onExecute: () => navigate('/admin') },
    { id: 'a-upgrade', label: 'Upgrade', description: 'Open checkout', icon: <Zap size={16} />, category: 'action', onExecute: openCheckout },
  ], [navigate, openCheckout]);

  // Action items
  const actionItems: CommandItem[] = useMemo(() => [
    { id: 'a-new-deal', label: 'Create deal', description: 'Start a new financing deal', icon: <Plus size={16} />, category: 'action', onExecute: () => openModal('newDeal') },
    { id: 'a-upload', label: 'Upload term sheet', description: 'Upload a PDF document', icon: <Upload size={16} />, category: 'action', onExecute: () => navigate('/upload') },
    {
      id: 'a-theme',
      label: `Switch to ${theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'} mode`,
      description: `Currently: ${theme}`,
      icon: theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />,
      category: 'action',
      onExecute: () => toggleTheme(),
    },
  ], [navigate, openModal, theme, toggleTheme]);

  // Deal items from query cache
  const dealItems: CommandItem[] = useMemo(() => {
    const dealsData = queryClient.getQueryData<{ deals?: Array<{ id: number; deal_name?: string; name?: string }> }>(['deals']);
    const deals = dealsData?.deals || [];
    return deals.slice(0, 10).map((deal) => ({
      id: `d-${deal.id}`,
      label: deal.deal_name || deal.name || 'Untitled deal',
      icon: <Briefcase size={16} />,
      category: 'deal' as const,
      onExecute: () => navigate(`/deals/${deal.id}`),
    }));
  }, [queryClient, navigate]);

  // Filter items by query
  const filteredItems = useMemo(() => {
    const all = [...pageItems, ...dealItems, ...actionItems];
    if (!query.trim()) {return all;}
    const q = query.toLowerCase();
    return all.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    );
  }, [query, pageItems, dealItems, actionItems]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: { label: string; items: CommandItem[] }[] = [];
    const pages = filteredItems.filter((i) => i.category === 'page');
    const deals = filteredItems.filter((i) => i.category === 'deal');
    const actions = filteredItems.filter((i) => i.category === 'action');
    if (pages.length) {groups.push({ label: 'Pages', items: pages });}
    if (deals.length) {groups.push({ label: 'Deals', items: deals });}
    if (actions.length) {groups.push({ label: 'Actions', items: actions });}
    return groups;
  }, [filteredItems]);

  // Flatten for keyboard navigation
  const flatItems = useMemo(() => groupedItems.flatMap((g) => g.items), [groupedItems]);

  // Execute selected item
  const executeItem = useCallback((item: CommandItem) => {
    closeCommandPalette();
    setQuery('');
    setSelectedIndex(0);
    item.onExecute();
  }, [closeCommandPalette]);

  // Reset state when opening
  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandPaletteOpen]);

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        useUIStore.getState().toggleCommandPalette();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Keyboard navigation inside palette
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[selectedIndex]) {
        executeItem(flatItems[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (query) {
        setQuery('');
      } else {
        closeCommandPalette();
      }
    }
  }, [flatItems, selectedIndex, executeItem, closeCommandPalette, query]);

  // Clamp selectedIndex when items change
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, Math.max(flatItems.length - 1, 0)));
  }, [flatItems.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) {return;}
    const selected = listRef.current.querySelector('[data-selected="true"]');
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!commandPaletteOpen) {return null;}

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) {closeCommandPalette();} }}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        style={{ animation: 'modalFadeIn 200ms ease' }}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className="relative mt-[20vh] w-full max-w-[480px] mx-4 h-fit max-h-[400px] bg-surface-card border border-border shadow-lg flex flex-col"
        style={{ animation: 'modalEnter 200ms ease' }}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 border-b border-border">
          <Search size={16} className="text-txt-tertiary flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, deals, actions..."
            className="flex-1 h-11 bg-transparent text-body text-txt-primary placeholder:text-txt-tertiary focus:outline-none"
            role="combobox"
            aria-expanded={flatItems.length > 0}
            aria-controls="command-results"
            aria-activedescendant={flatItems[selectedIndex]?.id}
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-overline text-txt-tertiary bg-surface-muted border border-border">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} id="command-results" className="overflow-y-auto flex-1" role="listbox">
          {groupedItems.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-body-sm text-txt-tertiary">No results found.</p>
            </div>
          ) : (
            groupedItems.map((group) => (
              <div key={group.label} role="group" aria-label={group.label}>
                <div className="px-4 pt-3 pb-1">
                  <span className="text-overline text-txt-tertiary uppercase">{group.label}</span>
                </div>
                {group.items.map((item) => {
                  const flatIndex = flatItems.indexOf(item);
                  const isSelected = flatIndex === selectedIndex;

                  return (
                    <button
                      key={item.id}
                      id={item.id}
                      data-selected={isSelected}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => executeItem(item)}
                      onMouseEnter={() => setSelectedIndex(flatIndex)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-4 h-10 text-left transition-colors',
                        isSelected
                          ? 'bg-surface-subtle text-txt-primary'
                          : 'text-txt-secondary hover:bg-surface-subtle'
                      )}
                    >
                      <span className={isSelected ? 'text-orange' : 'text-txt-tertiary'}>
                        {item.icon}
                      </span>
                      <span className="text-body flex-1 truncate">{item.label}</span>
                      {item.description && (
                        <span className="text-caption text-txt-tertiary truncate max-w-[140px]">
                          {item.description}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-border flex items-center gap-4 text-caption text-txt-tertiary">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">↵</kbd> open</span>
          <span><kbd className="font-mono">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}